import { useCallback, useState } from "react";

import { UseFormReturn } from "react-hook-form";

import { trpc } from "@/helpers/trpc";
import { useEmailEditorContext } from "@/src/contexts/EmailEditorContext";
import { EmailEditorFormData } from "@/types/email";

type UseThankYouEmailGenerationOptions = {
  form: UseFormReturn<EmailEditorFormData>;
  handleBodyChange: (newBody: string) => void;
  onEmailGenerated?: (body: string) => void;
  onLoadingChange?: (isLoading: boolean) => void;
};

export const useThankYouEmailGeneration = ({
  form,
  handleBodyChange,
  onEmailGenerated,
  onLoadingChange,
}: UseThankYouEmailGenerationOptions) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const { mutateAsync: generateThankYouEmail } = trpc.generateExtraThankYouEmailContent.useMutation();
  const { saveThankYouEmailToCache } = useEmailEditorContext();

  const generateEmail = useCallback(
    async (transactionId: string, length?: "shorter" | "longer" | "regenerate") => {
      setIsGenerating(true);
      onLoadingChange?.(true);

      const { data } = await generateThankYouEmail({
        transactionId,
        length,
      });

      if (data?.body) {
        handleBodyChange(data.body);
        form.setValue("body", data.body, { shouldDirty: true });
        saveThankYouEmailToCache(transactionId, data.body);
        onEmailGenerated?.(data.body);
      }

      setIsGenerating(false);
      onLoadingChange?.(false);
    },
    [form, generateThankYouEmail, handleBodyChange, onEmailGenerated, onLoadingChange, saveThankYouEmailToCache]
  );

  return {
    generateEmail,
    isGenerating,
  };
};
